import { HttpErrorResponse } from "@angular/common/http";
import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { DxFormComponent } from "devextreme-angular";
import notify from "devextreme/ui/notify";
import { forkJoin } from "rxjs";
import {
    FileTransfersService,
    IOcbCUFileTransfer,
    IOcbFileTransfer,
    IOcbFileTransferInfo,
    IOcbLocalAgent,
    IOcbPgpKey,
    IOcbRemotePlatform,
    LocalAgentsService,
    PgpKeysService,
    RemotePlatformsService,
    TagsService
} from "../../core";
import { FileTransferExecutionComponent } from "../file-transfer-execution-popup/file-transfer-execution-popup.component";
import { confirm } from "devextreme/ui/dialog";
import { LocalAgentCheckConnectionComponent } from "../local-agent-check-connection-popup/local-agent-check-connection-popup.component";

@Component({
    selector: 'app-file-transfer-form',
    templateUrl: './file-transfer-form.component.html',
    styleUrls: ['./file-transfer-form.component.scss'],
})
export class FileTransferFormComponent {
    @ViewChild('fileTransferForm') fileTransferForm?: DxFormComponent;
    @ViewChild('fileTransferExecution') ftExecution: FileTransferExecutionComponent;
    @ViewChild('checkConnectionPopup') checkConnectionPopup: LocalAgentCheckConnectionComponent;

    @Input() environmentId: string;
    @Input() fileTransfer: IOcbFileTransfer;
    @Input() newFileTransfer: boolean;
    @Input() showProgressBar: boolean;

    @Output() onFileTransferChanged: EventEmitter<IOcbFileTransfer> = new EventEmitter<IOcbFileTransfer>();

    public readOnly: boolean = true;

    public currentEnvironmentDecryptionKeys: IOcbPgpKey[];
    public currentEnvironmentEncryptionKeys: IOcbPgpKey[];
    public currentEnvironmentFileTransfersInfo: IOcbFileTransferInfo[];
    public currentEnvironmentLocalAgents: IOcbLocalAgent[];
    public currentEnvironmentRemotePlatforms: IOcbRemotePlatform[];
    public tags: string[] = new Array<string>();

    private expectedFormatRelativeMessage = '';
    private expectedFormatUncMessage = '';

    public createLogForIgnoredEmptyFilesOptions?;
    public extractZipArchiveInTargetFolderOptions;
    public ignoreEmptyFilesOptions;
    public overwriteFileInTargetFolderOptions?;
    public removeEmptyFilesOptions?;
    public replaceSpecialCharactersInFileNameOptions?;
    public replaceUnixLineFeedCharacterOptions?;

    // Regex
    public regexRelativePath = new RegExp(/^[\/]/);
    public regexUncPath = new RegExp(/^[\\\/]{2,}[^\\\/]+[\\\/]+[^\\\/]+/);
    
    constructor(private route: ActivatedRoute,
        private fileTransfersService: FileTransfersService,
        private agentsService: LocalAgentsService,
        private keysService: PgpKeysService,
        private platformsService: RemotePlatformsService,
        private router: Router,
        private tagsService: TagsService,
        private translateService: TranslateService) {
        this.extractZipArchiveInTargetFolderOptions = {
            onValueChanged: (e) => this.onExtractZipArchiveInTargetFolder(e)
        };
        this.ignoreEmptyFilesOptions = {
            onValueChanged: (e) => this.onIgnoreEmptyFilesChanged(e)
        };

        this.createLogForIgnoredEmptyFilesOptions = { disabled: true };
        this.overwriteFileInTargetFolderOptions = { disabled: false };
        this.replaceSpecialCharactersInFileNameOptions = { disabled: false };
        this.replaceUnixLineFeedCharacterOptions = { disabled: false };
        this.removeEmptyFilesOptions = { disabled: true };

        this.getAckFolderValidatorFormat = this.getAckFolderValidatorFormat.bind(this);
        this.getArchivesFolderValidatorFormat = this.getArchivesFolderValidatorFormat.bind(this);
        this.getSourceFolderValidatorFormat = this.getSourceFolderValidatorFormat.bind(this);
        this.getTargetFolderValidatorFormat = this.getTargetFolderValidatorFormat.bind(this);
        this.validateAckFolderFormat = this.validateAckFolderFormat.bind(this);
        this.validateArchivesFolderFormat = this.validateArchivesFolderFormat.bind(this);
        this.validateSourceFolderFormat = this.validateSourceFolderFormat.bind(this);
        this.validateTargetFolderFormat = this.validateTargetFolderFormat.bind(this);

        forkJoin({
            relMessage: this.translateService.get('_validators.expextedFormatRelative'),
            uncMessage: this.translateService.get('_validators.expextedFormatUnc')
        }).subscribe({
            next: (messages) => {
                this.expectedFormatRelativeMessage = messages.relMessage;
                this.expectedFormatUncMessage = messages.uncMessage;
            },
            error: (e) => this.handleError(e)
        });
    }


    private getSaveModel() {
        return {
            environmentId: this.environmentId,
            name: this.fileTransfer.name,
            localAgentId: this.fileTransfer.localAgent.id,
            fileNamePattern: this.fileTransfer.fileNamePattern,
            deleteTransferredFileFromSource: this.fileTransfer.deleteTransferredFileFromSource,
            ackManagement: this.fileTransfer.ackManagement,
            ackokExtension: this.fileTransfer.ackokExtension,
            ackkoExtension: this.fileTransfer.ackkoExtension,
            renamingParametersSeparator: this.fileTransfer.renamingParametersSeparator,
            renameOnlyArchivedFiles: this.fileTransfer.renameOnlyArchivedFiles,
            replaceSpecialCharactersInFileName: this.fileTransfer.replaceSpecialCharactersInFileName,
            overwriteFileInTargetFolder: this.fileTransfer.overwriteFileInTargetFolder,
            sendFileInNotificationEmail: this.fileTransfer.sendFileInNotificationEmail,
            sourceId: this.fileTransfer.source ? this.fileTransfer.source.id : null,
            targetId: this.fileTransfer.target ? this.fileTransfer.target.id : null,
            decryptionKeyId: this.fileTransfer.decryptionKey ? this.fileTransfer.decryptionKey.id : null,
            encryptionKeyId: this.fileTransfer.encryptionKey ? this.fileTransfer.encryptionKey.id : null,
            archivesId: this.fileTransfer.archives ? this.fileTransfer.archives.id : null,
            sourceFolder: this.fileTransfer.sourceFolder,
            targetFolder: this.fileTransfer.targetFolder,
            ackFolder: this.fileTransfer.ackFolder,
            archivesFolder: this.fileTransfer.archivesFolder,
            encryptArchivedFile: this.fileTransfer.encryptArchivedFile,
            executionMode: this.fileTransfer.executionMode,
            executeOnlyAfterId: this.fileTransfer.executeAfter?.id ? this.fileTransfer.executeAfter?.id : null,
            ignoreEmptyFiles: this.fileTransfer.ignoreEmptyFiles,
            createLogForIgnoredEmptyFiles: this.fileTransfer.createLogForIgnoredEmptyFiles,
            removeEmptyFiles: this.fileTransfer.removeEmptyFiles,
            extractZipArchiveInTargetFolder: this.fileTransfer.extractZipArchiveInTargetFolder,
            replaceUnixLineFeedCharacter: this.fileTransfer.replaceUnixLineFeedCharacter,
            sourceTags: this.fileTransfer.sourceTags,
            targetTags: this.fileTransfer.targetTags
        } as IOcbCUFileTransfer;
    }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }

    private showCheckConnectionPopup(platform: IOcbRemotePlatform) {
        if (this.fileTransfer.localAgent) this.checkConnectionPopup.agentId = this.fileTransfer.localAgent.id;
        this.checkConnectionPopup.remotePlatformId = platform.id;
        this.checkConnectionPopup.visible = true;
    }

    private validateFolderFormat(path?: string, platform?: IOcbRemotePlatform) {
        if (!platform) return !path || this.regexUncPath.test(path);
        else return this.regexRelativePath.test(path);
    }


    public checkConnectionToArchives() { this.showCheckConnectionPopup(this.fileTransfer.archives); }

    public checkConnectionToSource() { this.showCheckConnectionPopup(this.fileTransfer.source); }

    public checkConnectionToTarget() { this.showCheckConnectionPopup(this.fileTransfer.target); }

    public copy() {
        forkJoin({
            confirmMsg: this.translateService.get('fileTransfers.msgCopyConfirm'),
            confirmTitle: this.translateService.get('fileTransfers.msgCopyConfirmTitle')
        }).subscribe({
            next: (messages) => {
                confirm(messages.confirmMsg, messages.confirmTitle).then((confirmed) => {
                    if (!confirmed) return;
                    this.showProgressBar = true;
                    this.fileTransfersService.copyFileTransfer(this.fileTransfer.id).subscribe({
                        next: (fileTransfer) => {
                            this.router.navigateByUrl(`/${this.environmentId}/file-transfers/${fileTransfer.id}`);
                        },
                        error: (error) => {
                            this.showProgressBar = false;
                            this.handleError(error);
                        }
                    });
                });
            },
            error: (err: any) => this.handleError(err)
        });
    }

    public delete() {
        forkJoin({
            confirmMsg: this.translateService.get('fileTransfers.msgDeleteConfirm'),
            confirmTitle: this.translateService.get('fileTransfers.msgDeleteConfirmTitle'),
            doneMsg: this.translateService.get('fileTransfers.msgFileTransferDeleteComplete')
        }).subscribe({
            next: (data) => {
                let confirmResult = confirm(data.confirmMsg, data.confirmTitle);
                confirmResult.then((result) => {
                    if (!result) return;
                    this.showProgressBar = true;
                    this.fileTransfersService.deleteFileTransfer(this.fileTransfer.id).subscribe({
                        next: () => {
                            this.showProgressBar = false;
                            notify(data.doneMsg, 'success', 3000);
                            this.router.navigateByUrl(`/${this.environmentId}/file-transfers`);
                        },
                        error: (e) => {
                            this.showProgressBar = false;
                            this.handleError(e);
                        }
                    });
                });
            },
            error: (e) => this.handleError(e)
        });
    }

    public disable() {
        this.showProgressBar = true;
        forkJoin({
            msg: this.translateService.get('fileTransfers.msgFileTransferDisableComplete'),
            result: this.fileTransfersService.disableFileTransfer(this.fileTransfer.id)
        }).subscribe({
            next: (results) => {
                this.fileTransfer.isDisabled = true;
                this.onFileTransferChanged.emit(this.fileTransfer);
                this.showProgressBar = false;
                notify(results.msg, 'success', 3000);
            },
            error: (e) => {
                this.handleError(e);
                this.showProgressBar = false;
            }
        });
    }

    public enable() {
        this.showProgressBar = true;
        forkJoin({
            msg: this.translateService.get('fileTransfers.msgFileTransferEnableComplete'),
            result: this.fileTransfersService.enableFileTransfer(this.fileTransfer.id)
        }).subscribe({
            next: (results) => {
                this.fileTransfer.isDisabled = false;
                this.onFileTransferChanged.emit(this.fileTransfer);
                this.showProgressBar = false;
                notify(results.msg, 'success', 3000);
            },
            error: (e) => {
                this.handleError(e);
                this.showProgressBar = false;
            }
        });
    }

    public execute() {
        this.ftExecution.fileTransferId = this.fileTransfer.id;
        this.ftExecution.visible = true;
    }

    public getAckFolderValidatorFormat() {
        return this.fileTransfer?.target ? this.expectedFormatRelativeMessage : this.expectedFormatUncMessage;
    }

    public getArchivesFolderValidatorFormat() {
        return this.fileTransfer?.archives ? this.expectedFormatRelativeMessage : this.expectedFormatUncMessage;
    }

    public getSourceFolderValidatorFormat() {
        return this.fileTransfer?.source ? this.expectedFormatRelativeMessage : this.expectedFormatUncMessage;
    }

    public getTargetFolderValidatorFormat() {
        return this.fileTransfer?.target ? this.expectedFormatRelativeMessage : this.expectedFormatUncMessage;
    }

    public loadData() {
        forkJoin({
            agents: this.agentsService.getLocalAgents(this.environmentId, false),
            decryptionKeys: this.keysService.getPgpKeys(this.environmentId, 'decryption'),
            encryptionKeys: this.keysService.getPgpKeys(this.environmentId, 'encryption'),
            platforms: this.platformsService.getRemotePlatforms(this.environmentId, null, false),
            transfers: this.fileTransfersService.getFileTransfersInfo(this.environmentId),
            tags: this.tagsService.getTags(this.environmentId)
        }).subscribe({
            next: (data) => {
                this.currentEnvironmentLocalAgents = data.agents;
                this.currentEnvironmentDecryptionKeys = data.decryptionKeys;
                this.currentEnvironmentEncryptionKeys = data.encryptionKeys;
                this.currentEnvironmentRemotePlatforms = data.platforms;
                this.currentEnvironmentFileTransfersInfo = data.transfers;
                if (!this.newFileTransfer) {
                    const index = this.currentEnvironmentFileTransfersInfo.findIndex((f) => f.id === this.fileTransfer.id);
                    if (index > -1) this.currentEnvironmentFileTransfersInfo.splice(index, 1);
                }
                this.tags = data.tags.map(t => t.value);
                if (this.newFileTransfer) this.readOnly = false;
                this.showProgressBar = false;
            },
            error: (error) => {
                this.handleError(error);
                this.showProgressBar = false;
            }
        });
    }

    public onArchivesPlatformChanged(e) { this.fileTransfer.archives = e.selectedItem; }

    public onExtractZipArchiveInTargetFolder(e) {
        if (!this.fileTransfer) return;
        if (e.value) {
            this.fileTransfer.overwriteFileInTargetFolder = true;
            this.fileTransfer.replaceUnixLineFeedCharacter = false;
            this.fileTransfer.replaceSpecialCharactersInFileName = false;
        }

        this.overwriteFileInTargetFolderOptions = { disabled: e.value };
        this.replaceSpecialCharactersInFileNameOptions = { disabled: e.value };
        this.replaceUnixLineFeedCharacterOptions = { disabled: e.value };
    }
    
    public onIgnoreEmptyFilesChanged(e) {
        if (!this.fileTransfer) return;
        if (!e.value) {
            this.fileTransfer.createLogForIgnoredEmptyFiles = false;
            this.fileTransfer.removeEmptyFiles = false;
        }

        this.createLogForIgnoredEmptyFilesOptions = { disabled: !e.value };
        this.removeEmptyFilesOptions = { disabled: !e.value };
    }

    public onFormSubmit(e) {
        if (this.readOnly) return;

        const validationResult = this.fileTransferForm.instance.validate();
        if (!validationResult.isValid) return;

        const saveModel = this.getSaveModel();
        if (this.newFileTransfer) {
            forkJoin({
                message: this.translateService.get('fileTransfers.msgFileTransferCreationComplete'),
                saveResult: this.fileTransfersService.addFileTransfer(saveModel)
            })
                .subscribe({
                    next: (results) => {
                        notify(results.message, 'success', 3000);
                        this.router.navigateByUrl(`/${this.environmentId}/file-transfers/${results.saveResult.id}`);
                    },
                    error: (error) => {
                        this.handleError(error);
                        this.showProgressBar = false;
                    }
                });
        }
        else {
            forkJoin({
                message: this.translateService.get('fileTransfers.msgFileTransferUpdateComplete'),
                saveResult: this.fileTransfersService.updateFileTransfer(this.fileTransfer.id, saveModel)
            })
                .subscribe({
                    next: (results) => {
                        notify(results.message, 'success', 3000);
                        this.showProgressBar = false;
                        this.onFileTransferChanged.emit(this.fileTransfer);
                    },
                    error: (error) => {
                        this.handleError(error);
                        this.showProgressBar = false;
                    }
                });
        }
    }

    public onSourcePlatformChanged(e) { this.fileTransfer.source = e.selectedItem; }

    public onTargetPlatformChanged(e) { this.fileTransfer.target = e.selectedItem; }

    public validateAckFolderFormat(e) {
        return !this.fileTransfer.ackManagement ? true : this.validateFolderFormat(e.value, this.fileTransfer?.target);
    }

    public validateArchivesFolderFormat(e) {
        console.log('validate archives folder');
        return this.validateFolderFormat(e.value, this.fileTransfer?.archives);
    }

    public validateSourceFolderFormat(e) {
        return this.validateFolderFormat(e.value, this.fileTransfer?.source);
    }

    public validateTargetFolderFormat(e) {
        return this.validateFolderFormat(e.value, this.fileTransfer?.target);
    }

}

export class SimpleObject {
    value: number | string;

    name: string;

    icon?: string;

    badge?: string;
}